import React, { useEffect, useState } from "react";
import LinkTargetSetting from "./LinkTargetSetting";
import PushToggle from "./PushToggle";

/**
 * AccountSettingsPage — 내 계정 / 세션 / 회원탈퇴
 *
 * Props:
 *  - API_BASE_URL
 *  - currentUserId
 *  - currentGroupCode
 *  - onLogout: () => void   // 로그아웃 후 호출 (탈퇴 / 강제 로그아웃 시)
 *  - onClose:  () => void   // 모달 닫기 (Settings 모달 호환)
 */
const AccountSettingsPage = ({
  API_BASE_URL,
  currentUserId,
  currentGroupCode,
  onLogout,
  onClose,
}) => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 탈퇴
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawPw, setWithdrawPw] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // 비밀번호 설정 (Phase 4 — OTP-only 사용자가 메인 진입 후 PW 만들기)
  const [setPwOpen, setSetPwOpen] = useState(false);
  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false); // PW 보기/숨기기 toggle

  const handleSetPassword = async () => {
    setPwMsg("");
    if (!newPw1 || newPw1.length < 8) {
      setPwMsg("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (newPw1 !== newPw2) {
      setPwMsg("비밀번호가 일치하지 않습니다.");
      return;
    }
    setPwSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/set-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          newPassword: newPw1,
          newPasswordConfirm: newPw2,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setPwMsg("비밀번호가 설정되었습니다. 다음 로그인부터 사용 가능.");
        setNewPw1("");
        setNewPw2("");
        setTimeout(() => {
          setSetPwOpen(false);
          setPwMsg("");
        }, 2000);
      } else {
        setPwMsg(data.message || "비밀번호 설정 실패");
      }
    } catch (e) {
      setPwMsg("서버 연결 실패");
    } finally {
      setPwSaving(false);
    }
  };

  const token = () => localStorage.getItem("token");

  const fetchSessions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/sessions`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (data.success) {
        setSessions(data.sessions || []);
        setCurrentSessionId(data.currentSessionId || null);
      } else {
        setError(data.message || "세션 조회에 실패했습니다.");
      }
    } catch (e) {
      setError("서버 연결 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(); /* eslint-disable-next-line */
  }, []);

  const handleRevoke = async (sessionId) => {
    if (!window.confirm("이 세션을 종료할까요?")) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/sessions/${sessionId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token()}` },
        },
      );
      const data = await res.json();
      if (data.success) {
        if (sessionId === currentSessionId) {
          // 자기 자신 세션 종료 → 로그아웃
          onLogout && onLogout();
        } else {
          await fetchSessions();
        }
      } else {
        alert(data.message || "세션 종료 실패");
      }
    } catch (e) {
      alert("서버 오류");
    }
  };

  const handleLogout = async () => {
    if (!window.confirm("로그아웃할까요?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
    } catch (_) {}
    onLogout && onLogout();
  };

  const handleWithdraw = async () => {
    if (!withdrawConfirm) {
      alert('"이 작업은 되돌릴 수 없습니다" 체크박스를 확인해주세요.');
      return;
    }
    setWithdrawing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/withdraw`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: withdrawPw,
          reason: withdrawReason || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert("회원탈퇴가 완료되었습니다.\n이용해주셔서 감사합니다.");
        onLogout && onLogout();
      } else {
        alert(data.message || "탈퇴 처리 실패");
      }
    } catch (e) {
      alert("서버 오류");
    } finally {
      setWithdrawing(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(iso);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>내 계정 설정</h2>
        {onClose && (
          <button style={styles.closeBtn} onClick={onClose} aria-label="닫기">
            ×
          </button>
        )}
      </div>

      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <h3 style={styles.sectionTitle}>계정 정보</h3>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>그룹코드</span>
          <span>{currentGroupCode || "-"}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>사용자 ID</span>
          <span>{currentUserId || "-"}</span>
        </div>
        <div style={styles.actionsRow}>
          <button onClick={handleLogout} style={styles.outlineBtn}>
            로그아웃
          </button>
          <button
            onClick={() => setSetPwOpen(!setPwOpen)}
            style={{
              ...styles.outlineBtn,
              borderColor: "#1a73e8",
              color: "#1a73e8",
            }}
          >
            {setPwOpen ? "비밀번호 설정 닫기" : "비밀번호 설정 / 변경"}
          </button>
        </div>
        {setPwOpen && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, color: "#1a73e8", fontWeight: 600 }}>
              비밀번호 설정 / 변경
            </div>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={newPw1}
                onChange={(e) => setNewPw1(e.target.value)}
                placeholder="새 비밀번호 (8자 이상)"
                style={{
                  width: "100%",
                  padding: "8px 36px 8px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  fontSize: 13,
                  boxSizing: "border-box",
                }}
                disabled={pwSaving}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  padding: "2px 4px",
                }}
                title={showPw ? "숨기기" : "보기"}
              >
                {showPw ? "숨김" : "보기"}
              </button>
            </div>
            <input
              type={showPw ? "text" : "password"}
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSetPassword();
              }}
              placeholder="비밀번호 확인"
              style={{
                padding: "8px 10px",
                border: "1px solid #d1d5db",
                borderRadius: 4,
                fontSize: 13,
              }}
              disabled={pwSaving}
            />
            <div
              style={{
                fontSize: 11,
                color: "#6b7280",
                padding: "4px 0",
                lineHeight: 1.4,
              }}
            >
              저장 후 로그아웃 → 재로그인 시 <b>"30일 자동 로그인"</b>{" "}
              체크하면 30일간 자동 진입.
            </div>
            {pwMsg && (
              <div
                style={{
                  fontSize: 12,
                  color: pwMsg.startsWith("비밀번호가 설정되었습니다")
                    ? "#0d8a4f"
                    : "#dc2626",
                }}
              >
                {pwMsg}
              </div>
            )}
            <button
              onClick={handleSetPassword}
              disabled={pwSaving}
              style={{
                padding: "8px 12px",
                background: "#1a73e8",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: pwSaving ? "not-allowed" : "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {pwSaving ? "저장 중..." : "비밀번호 저장"}
            </button>
          </div>
        )}
      </section>

      <section style={styles.section} data-testid="font-size-setting">
        <div style={styles.sectionHead}>
          <h3 style={styles.sectionTitle}>글자 크기</h3>
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8, lineHeight: 1.6 }}>
          화면 전체의 글자 크기를 바꿉니다. 이 브라우저에만 저장됩니다.
        </div>
        <button type="button" onClick={() => window.dispatchEvent(new Event("loginhs:open-font-size"))}
          style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>
          글자 크기 바꾸기
        </button>
      </section>

      <LinkTargetSetting styles={styles} />

      <PushToggle sectionStyle={styles.section} titleStyle={styles.sectionTitle} />

      <section style={styles.section}>
        <div style={styles.sectionHead}>
          <h3 style={styles.sectionTitle}>활성 세션</h3>
          <button
            onClick={fetchSessions}
            style={styles.linkBtn}
            disabled={loading}
          >
            {loading ? "불러오는 중..." : "새로고침"}
          </button>
        </div>
        {error && <div style={styles.error}>{error}</div>}
        {sessions.length === 0 && !loading && (
          <div style={styles.emptyText}>활성 세션이 없습니다.</div>
        )}
        <div style={styles.sessionList}>
          {sessions.map((s) => {
            const isCurrent = s.sessionId === currentSessionId;
            return (
              <div
                key={s.sessionId}
                style={{
                  ...styles.sessionItem,
                  ...(isCurrent ? styles.sessionCurrent : {}),
                }}
              >
                <div style={styles.sessionInfo}>
                  <div style={styles.sessionTitle}>
                    {s.deviceLabel || "Unknown device"}
                    {isCurrent && (
                      <span style={styles.currentBadge}>현재 세션</span>
                    )}
                  </div>
                  <div style={styles.sessionMeta}>
                    IP: {s.ipAddress || "-"} · 마지막 활동:{" "}
                    {formatTime(s.lastSeenAt)}
                  </div>
                  <div style={styles.sessionUA}>{s.userAgent || ""}</div>
                </div>
                <button
                  onClick={() => handleRevoke(s.sessionId)}
                  style={styles.revokeBtn}
                >
                  {isCurrent ? "로그아웃" : "종료"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section
        style={{
          ...styles.section,
          borderTop: "1px solid #fee2e2",
          paddingTop: "20px",
        }}
      >
        <div style={styles.sectionHead}>
          <h3 style={{ ...styles.sectionTitle, color: "#dc2626" }}>회원탈퇴</h3>
        </div>
        {!showWithdraw && (
          <>
            <p style={styles.warnText}>
              탈퇴 시 계정은 비활성화되며 개인정보는 anonymize 처리됩니다.
              <br />
              같은 이메일로 다시 가입할 수 있지만, 이전 기록은 복구되지
              않습니다.
            </p>
            <button
              onClick={() => setShowWithdraw(true)}
              style={styles.dangerOutlineBtn}
            >
              회원탈퇴 시작
            </button>
          </>
        )}
        {showWithdraw && (
          <div style={styles.withdrawForm}>
            <label style={styles.label}>
              비밀번호 재확인 (없으면 비워두세요)
            </label>
            <input
              type="password"
              value={withdrawPw}
              onChange={(e) => setWithdrawPw(e.target.value)}
              placeholder="비밀번호"
              style={styles.input}
              disabled={withdrawing}
            />
            <label style={{ ...styles.label, marginTop: "12px" }}>
              탈퇴 사유 (선택)
            </label>
            <textarea
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              placeholder="개선에 참고할 수 있도록 사유를 알려주세요"
              rows={3}
              style={{ ...styles.input, resize: "vertical" }}
              disabled={withdrawing}
            />
            <label style={styles.confirmCheck}>
              <input
                type="checkbox"
                checked={withdrawConfirm}
                onChange={(e) => setWithdrawConfirm(e.target.checked)}
                disabled={withdrawing}
              />
              <span>
                이 작업은 되돌릴 수 없습니다. 정말로 탈퇴하시겠습니까?
              </span>
            </label>
            <div style={styles.actionsRow}>
              <button
                onClick={() => {
                  setShowWithdraw(false);
                  setWithdrawPw("");
                  setWithdrawReason("");
                  setWithdrawConfirm(false);
                }}
                style={styles.outlineBtn}
                disabled={withdrawing}
              >
                취소
              </button>
              <button
                onClick={handleWithdraw}
                style={styles.dangerBtn}
                disabled={withdrawing}
              >
                {withdrawing ? "처리 중..." : "회원탈퇴 확정"}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "640px",
    margin: "0 auto",
    padding: "24px",
    fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard', 'Noto Sans KR', -apple-system, sans-serif",
    color: "#1a1b1e",
    background: "#fff",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  title: { fontSize: "22px", fontWeight: 700, margin: 0 },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#6b7280",
  },
  section: { marginBottom: "28px" },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
    color: "#374151",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f3f4f6",
    fontSize: "14px",
  },
  infoLabel: { color: "#6b7280" },
  actionsRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "12px",
  },
  outlineBtn: {
    padding: "8px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#fff",
    color: "#374151",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: 500,
  },
  emptyText: { fontSize: "13px", color: "#9ca3af", padding: "12px 0" },
  error: {
    padding: "10px 14px",
    borderRadius: "6px",
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    fontSize: "13px",
    marginBottom: "8px",
  },
  sessionList: { display: "flex", flexDirection: "column", gap: "8px" },
  sessionItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#f9fafb",
  },
  sessionCurrent: { borderColor: "#18181b", background: "#fafafa" },
  sessionInfo: { flex: 1, minWidth: 0 },
  sessionTitle: { fontSize: "14px", fontWeight: 600, marginBottom: "4px" },
  currentBadge: {
    marginLeft: "8px",
    padding: "2px 6px",
    borderRadius: "4px",
    background: "#18181b",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 500,
  },
  sessionMeta: { fontSize: "12px", color: "#6b7280", marginBottom: "4px" },
  sessionUA: {
    fontSize: "11px",
    color: "#9ca3af",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  revokeBtn: {
    padding: "6px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    background: "#fff",
    color: "#dc2626",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
    marginLeft: "12px",
  },
  warnText: {
    fontSize: "13px",
    color: "#6b7280",
    lineHeight: 1.6,
    marginBottom: "12px",
  },
  dangerOutlineBtn: {
    padding: "10px 16px",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    background: "#fff",
    color: "#dc2626",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  dangerBtn: {
    padding: "10px 16px",
    border: "none",
    borderRadius: "8px",
    background: "#dc2626",
    color: "#fff",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 600,
  },
  withdrawForm: { display: "flex", flexDirection: "column" },
  label: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "4px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },
  confirmCheck: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginTop: "12px",
    fontSize: "13px",
    color: "#374151",
    cursor: "pointer",
  },
};

export default AccountSettingsPage;
